/**
 * Provided a children prop and a set of props, this function will
 * return the children prop if it is a ReactNode, or call the children
 * prop with the provided props if it is a function.\
 * Useful for context providers.
 *
 * @typeParam P - The props type (infered)
 * @typeParam C - The children type (infered)
 *
 * @param children ReactNode or function that returns a ReactNode
 * @param props Props to pass to the children function
 * @returns ReactNode
 *
 * @example
 * const StuffProvider = (
 * 	{children, ...props}: ProviderProps,
 * 	ref: ProviderRef,
 * ) => {
 * 	const stuff	= useStuff(props);
 * 	useImperativeHandle(ref, () => stuff, [stuff]);
 * 	return (
 * 		<Stuff.Provider value={stuff}>
 * 			{renderChildrenOrChildrenFunction(children, stuff)}
 * 		</Stuff.Provider>
 * 	);
 * };
 */
export const renderChildrenOrChildrenFunction = <
	P extends {},
	C extends React.ReactNode | ((props: P) => React.ReactNode),
>(
	children: C,
	props: P,
): React.ReactNode =>
	typeof children === 'function' ? children(props) : children;

export default renderChildrenOrChildrenFunction;
